import { type TerminalResource, TerminalsController_register_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { desktopTerminal } from '@/desktop/bridge';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { useReturnToTill } from '../till/return-to-till';
import { messageForLeaseRejection } from './lease-messages';
import { stateOf } from './terminal-states';

/**
 * The moment a row becomes a device: `POST /v1/terminals/{id}/register`.
 *
 * **The response is the thing.** It carries a signed lease, and that lease is
 * what authorises a till — it is not a receipt for the registration, it is the
 * credential the machine will authenticate with until it renews. So this screen
 * has one job after the call returns: put it somewhere a person can carry it to
 * the machine, say plainly that it will not be shown again, and then let go of
 * it. It is held in this component's state and nowhere else — not in storage,
 * not in the URL, not in a log line, not in a value that survives this dialog
 * closing. The suite asserts each of those, because "we did not persist it" is
 * exactly the kind of claim that quietly stops being true.
 *
 * **The key is minted once and reused.** Every `@Idempotent` route refuses a
 * request with no `Idempotency-Key`, and this one uses it for more than
 * bookkeeping: a retry carrying the first key is answered with the first
 * response, so a dropped answer can be asked for again instead of registering a
 * second time. Minting a fresh key on the retry is precisely the mistake that
 * once handed a device a lease belonging to another registration — see
 * `lib/idempotency-key.ts`. The key therefore lives for as long as this
 * component does: every press of the button below is the *same* attempt, and a
 * genuinely new registration is a new open of this dialog.
 *
 * **Registering a terminal that is already in service is allowed**, and the API
 * says why: it supersedes the lease the device holds rather than refusing, which
 * is the only way back for a machine that has lost its lease or is being
 * replaced. It is also a way to stop a working till by accident, so it is a
 * separate button with its own warning rather than the same one worded softly.
 */

export function RegisterTerminalDialog({
  terminal,
  outletNames,
  onClose,
  onRegistered,
}: {
  /** The terminal being registered, or `null` when nothing is. */
  terminal: TerminalResource | null;

  /**
   * What the park's outlets are called, by id — the names this screen already
   * shows. Handed to the machine with its registration, because a till selling
   * for more than one outlet (#243) must say which, and nothing a terminal can
   * read names an outlet.
   */
  outletNames: ReadonlyMap<string, string>;

  onClose: () => void;

  /** The lease has been issued; the row's status on the list has changed. */
  onRegistered: () => void;
}) {
  return (
    <Dialog
      open={terminal !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {/* A child, so closing unmounts it: the lease goes with it, and so does
            the idempotency key — the next open is a different registration and
            must not replay this one's answer. */}
        {terminal !== null && (
          <RegisterTerminalBody
            terminal={terminal}
            outletNames={outletNames}
            onClose={onClose}
            onRegistered={onRegistered}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RegisterTerminalBody({
  terminal,
  outletNames,
  onClose,
  onRegistered,
}: {
  terminal: TerminalResource;
  outletNames: ReadonlyMap<string, string>;
  onClose: () => void;
  onRegistered: () => void;
}) {
  const client = useApiClient();

  // One key for this dialog's whole life. `useState` with an initialiser rather
  // than a `useRef` assignment, so it is minted once even under a double render
  // and never on a re-render that follows a failure.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [lease, setLease] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const supersedes = stateOf(terminal) === 'IN_SERVICE';

  // Read once per render rather than held: the bridge is injected by the preload
  // script before this bundle runs and cannot appear or vanish while a dialog is
  // open. `null` is the browser, which is the ordinary case.
  const device = desktopTerminal();

  const register = async () => {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      const issued = await client.request(TerminalsController_register_v1, {
        path: { id: terminal.id },
        headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
      });

      onRegistered();

      if (device === null) {
        setLease(issued.lease);
        return;
      }

      // The machine this is running on is the machine being registered, so the
      // lease goes straight across and is never displayed. The whole response
      // crosses, not the lease alone: the device verifies the signature against
      // the public key beside it, and a lease without its key is a value nothing
      // can check.
      //
      // The outlet names ride beside it as display text. The device keeps the
      // ones for outlets it holds a lease at and decides nothing by them.
      const outcome = await device.register(issued, Object.fromEntries(outletNames));

      if (outcome.accepted) {
        setClaimed(true);
        return;
      }

      // The registration stands — a lease was issued and any previous one is
      // superseded — and what failed is this device taking it. Pressing the
      // button again replays the same answer under the same idempotency key
      // rather than registering a second time, which is exactly the retry this
      // needs.
      setFailure(messageForLeaseRejection(outcome.reason));
    } catch (error) {
      // `messageForFailure` and nothing else. The error is not logged, because
      // an `ApiError` from this route is one of the few whose `details` could
      // conceivably carry the request back at us.
      setFailure(messageForFailure(error));
    } finally {
      setPending(false);
    }
  };

  if (claimed) return <ClaimedByThisDevice terminal={terminal} />;

  if (lease !== null) return <IssuedLease terminal={terminal} lease={lease} onDone={onClose} />;

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {supersedes ? 'Re-register' : 'Register'} {terminal.name}
        </DialogTitle>
        <DialogDescription>
          This issues the lease that lets{' '}
          <span className="font-mono text-foreground numeric">{terminal.prefix}</span> take money —
          the credential the machine authenticates with.
        </DialogDescription>
      </DialogHeader>

      {/* `text-foreground` on `bg-muted`: the muted-on-muted pairing this panel
          invites measures 4.39:1 and misses AA. */}
      <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        {device === null
          ? // Said plainly, because the alternative is an owner at a desk issuing
            // a lease, superseding a working till's, and being handed a value
            // with nowhere to put it. There is no screen on a device that takes
            // a lease typed in; the way a machine gets one is this dialog opened
            // on the machine.
            'A terminal takes its lease from this screen opened on the machine itself. Registering from a browser issues one and cannot deliver it — and if this terminal is already in service, it stops the device that holds the old one.'
          : 'This machine takes the lease itself. Nothing is shown and nothing has to be copied, and it opens on the till as soon as it holds one.'}
      </p>

      {supersedes && (
        // Not an error, and not styled as one. It is the consequence of the
        // button, said before it is pressed rather than discovered at the
        // counter when the till stops answering.
        <p className="rounded-2xl border border-border px-4 py-3 text-sm text-foreground">
          This terminal is already in service. A new lease supersedes the one the device is using,
          and that device stops working the next time it reaches the server. Do this only if the
          machine has lost its lease or is being replaced.
        </p>
      )}

      {failure !== null && (
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={() => void register()} disabled={pending}>
          {pending ? 'Registering…' : 'Register terminal'}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * The machine registered itself, and the lease was never on screen.
 *
 * The other outcome below hands somebody a hundred characters of base64 to
 * carry; this one is the whole reason that is worth avoiding. The device already
 * holds the credential, the sentence about it being shown once has nothing to
 * warn about, and the only thing left is to go to the till.
 *
 * **The button navigates rather than instructing.** The shell opens on the till
 * from the next start — the main process reads the lease at boot and picks the
 * route — but nobody is going to restart a terminal they have just set up, and
 * "close this and reopen the app" is an instruction a screen should be able to
 * carry out itself.
 */
function ClaimedByThisDevice({ terminal }: { terminal: TerminalResource }) {
  const returnToTill = useReturnToTill();

  return (
    <>
      <DialogHeader>
        <DialogTitle>This machine is {terminal.name}</DialogTitle>
        <DialogDescription>
          It holds its lease. Nothing was displayed and there is nothing to carry.
        </DialogDescription>
      </DialogHeader>

      <Alert role="status">
        <IconCircleCheck />
        <AlertDescription>
          <span className="font-mono text-foreground numeric">{terminal.prefix}</span> is in
          service. Every ticket this machine prints carries that prefix, and no other machine will
          use it.
        </AlertDescription>
      </Alert>

      {/* `text-foreground` on `bg-muted`: the muted-on-muted pairing measures
          4.39:1 and misses AA. */}
      <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        From now on this machine starts at the till. The back office is still reachable from it, but
        a cashier does not need one — they sign in at the till with their staff code and PIN.
      </p>

      <DialogFooter>
        {/* Signs the back office out on the way (#259): the owner who
            registered this machine is done here, and a session left behind is
            one left on a shared terminal. */}
        <Button onClick={returnToTill}>Open the till</Button>
      </DialogFooter>
    </>
  );
}

/**
 * The lease, once.
 *
 * A read-only field rather than a paragraph, because it is a value to be taken
 * rather than read: it selects on focus, it copies with the button, and it sits
 * in the mono face with tabular figures like every other value in this system
 * that gets typed back in somewhere else.
 *
 * The clipboard is a deliberate exception to "nothing outside this screen holds
 * it" — it is the transfer the ticket asks for, it happens only when somebody
 * presses the button, and the alternative is staff hand-copying a hundred
 * characters of base64 at a counter, which is a mis-typed lease and a support
 * call rather than a security improvement.
 */
function IssuedLease({
  terminal,
  lease,
  onDone,
}: {
  terminal: TerminalResource;
  lease: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    // Guarded rather than optional-chained: `await undefined` resolves, and the
    // button would then say it had copied something it had not.
    if (typeof navigator.clipboard?.writeText !== 'function') return;

    try {
      await navigator.clipboard.writeText(lease);
      setCopied(true);
    } catch {
      // Nothing is reported and nothing is logged — the field is still there
      // and still selectable, which is the fallback.
      setCopied(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{terminal.name} is registered</DialogTitle>
        <DialogDescription>
          Take the lease to the device now. It is shown once and cannot be read again.
        </DialogDescription>
      </DialogHeader>

      <Alert role="status">
        <IconCircleCheck />
        <AlertDescription>
          <span className="font-mono text-foreground numeric">{terminal.prefix}</span> is in
          service. It can issue tickets as soon as the device holds this lease.
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label htmlFor="terminal-lease" className={fieldLabel}>
          Device lease
        </Label>

        <div className="flex items-start gap-2">
          <Input
            id="terminal-lease"
            readOnly
            value={lease}
            spellCheck={false}
            autoComplete="off"
            className={`${fieldSurface} h-11 flex-1 font-mono numeric`}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button variant="outline" className="h-11" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy lease'}
          </Button>
        </div>

        <p id="terminal-lease-hint" className="text-sm text-muted-foreground">
          Closing this discards it. If it is lost, register the terminal again — that issues a new
          lease and stops this one from working.
        </p>
      </div>

      <DialogFooter>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </>
  );
}
